import { defineStore } from 'pinia';
import { StoreModuleName } from '../../constants';
import { CustomFetchContext } from '~/src/api/requests/types';
import { ExamResultPageStatus, ExamResultStatus } from './types';
import {
  updateChapterPracticeData,
  updateDailyPunchData,
  updateExamResultData,
  updateListExamResultData,
  updateWrongQuestionResult,
  updateCollectionResult,
  updateKnowledgePracticeResult,
} from './helper';
import { AnswerModeEnum, ExamModeEnum } from '~/src/constants/exam';
import {
  fetchContinueDailyPunchCount,
  fetchContinueDailyPunchActCount,
} from '~/src/api/modules/exam';
import dayjs from 'dayjs';
import { useEnvironmentStore } from '../environment';
import { useUserStore } from '../user';
import { USER_CENTER_SUBJECT_STORAGE_KEY } from '~/src/constants/exam/storage';

export const useExamResultStore = defineStore(StoreModuleName.EXAM_RESULT, () => {
  const envStore = useEnvironmentStore();
  const userStore = useUserStore();
  const isMobile = computed(() => envStore.isMobile);

  const createInitState = (): ExamResultPageStatus => {
    return {
      meta: {
        mode: ExamModeEnum.PunchInDaily,
        title: '',
        punchCardTime: '',
        status: '',
        isFinished: false,
        sectionId: '',
        chapterId: '',
        id: '',
        date: '',
        number: '',
        userSubject: '',
      },
      dashboard: {
        total: 0,
        done: 0,
        undo: 0,
        success: 0,
        failed: 0,
        accuracy: 0,
        duration: 0,
        progress: 0,
        submitTotalQuestion: 0,
        todayTotalQuestion: 0,
        continueSignCount: 0,
        totalSignCount: 0,
        signTop: 0,
      },
      questList: [],
    };
  };

  const pageStatus = reactive<ExamResultPageStatus>(createInitState());

  const resetState = () => {
    Object.assign(pageStatus, createInitState());
  };

  const initPageMeta = async () => {
    const route = useRoute();
    const answerId = route.query.answer_id as string;
    let answerMode = Number(route.query.answerMode) as AnswerModeEnum;
    // examId or sectionId
    const examId = route.params.id as string;
    const submitId = route.query.submit_id as string;
    const answerType = route.query.type as string;
    const date = route.query.date as string;
    const autoShowPoster = route.query.autoShowPoster as string;
    const routeUserSubject = route.query.userSubject as string;
    const highFrequency = route.query.highFrequency as string;
    const today = route.query.today as string;
    const number = route.query.number as string;

    let mode = null as unknown as ExamModeEnum;

    const checkRouteParams = () => {
      if (!((route.params.examMode as any) in ExamModeEnum)) {
        return false;
      }

      /* if (!/\d+/.test(route.query.answerId as any)) {
    return false;
  } */
      return true;
    };

    if (checkRouteParams()) {
      mode = route.params.examMode as unknown as ExamModeEnum;
      pageStatus.meta.mode = +mode;
      pageStatus.meta.answerId = answerId;

      if (!isMobile.value) {
        answerMode = answerMode || AnswerModeEnum.NormalExamMode;
      }

      answerMode && (pageStatus.meta.answerMode = answerMode as AnswerModeEnum);

      pageStatus.meta.answerType = answerType;
      pageStatus.meta.submitId = submitId;
      pageStatus.meta.date = date || dayjs().format('YYYY-MM-DD');
      pageStatus.meta.autoShowPoster = autoShowPoster;
      // userSubject
      const targetSubject = routeUserSubject || userStore.userMeta.subjectId;
      pageStatus.meta.userSubject = targetSubject;
      pageStatus.meta.highFrequency = highFrequency;
      pageStatus.meta.today = today;
      pageStatus.meta.id = examId;
      pageStatus.meta.number = number;

      if (pageStatus.meta.mode === ExamModeEnum.ChapterPractice) {
        pageStatus.meta.sectionId = examId;
      }
    } else {
      throw createError({
        statusCode: 500,
        statusMessage: 'route params invalid.',
      });
    }
  };

  const updateStoreData = async (fetchContext: CustomFetchContext) => {
    const { mode, submitId, answerId, sectionId, id, number, userSubject } = pageStatus.meta;
    let result = {} as ExamResultPageStatus;

    if (mode === ExamModeEnum.PunchInDaily) {
      result = await updateDailyPunchData(fetchContext, pageStatus);

      // 获取连续打卡数
      fetchContinueDailyPunchActCount().then(res => {
        pageStatus.dashboard.currentlyDayCount = Number(res.data) || 0;
      });
    } else if (mode === ExamModeEnum.ChapterPractice) {
      result = await updateChapterPracticeData(submitId!, sectionId!, fetchContext);
    } else if (mode === ExamModeEnum.NormalExam) {
      result = await updateExamResultData(answerId!, number!, fetchContext);
    } else if (mode === ExamModeEnum.ImitateExam || mode === ExamModeEnum.PreviousExam) {
      result = await updateListExamResultData(answerId!, number!, fetchContext);
    } else if (mode === ExamModeEnum.WrongQuestion) {
      result = await updateWrongQuestionResult(userSubject!, fetchContext);
    } else if (mode === ExamModeEnum.CollectionQuestion) {
      // 收藏夹的
      result = await updateCollectionResult(userSubject!, fetchContext);
    } else if (mode === ExamModeEnum.KnowledgeExam) {
      result = await updateKnowledgePracticeResult(submitId!, id!, fetchContext);
    }

    console.log('result', result.meta);
    pageStatus.meta = {
      ...pageStatus.meta,
      ...result.meta,
    };

    pageStatus.dashboard = {
      ...pageStatus.dashboard,
      ...result.dashboard,
    };

    pageStatus.questList = {
      ...result.questList,
    };
  };

  // footer btns
  const footerBtns = computed(() => {
    return {
      showRetry: pageStatus.meta.mode !== ExamModeEnum.ChapterPractice,
      showHighFrequencyQuest: [
        ExamModeEnum.NormalExam,
        ExamModeEnum.ImitateExam,
        ExamModeEnum.PreviousExam,
      ].includes(pageStatus.meta.mode),
      showChapterPractice:
        pageStatus.meta.mode === ExamModeEnum.PunchInDaily &&
        pageStatus.meta.status === ExamResultStatus.Failed,
      showContinue:
        pageStatus.meta.mode === ExamModeEnum.ChapterPractice ||
        pageStatus.meta.mode === ExamModeEnum.KnowledgeExam,
      showMockExam:
        pageStatus.meta.mode === ExamModeEnum.PunchInDaily &&
        pageStatus.meta.status === ExamResultStatus.Success,
      showChangeGroup:
        pageStatus.meta.mode === ExamModeEnum.PunchInDaily &&
        pageStatus.meta.status === ExamResultStatus.Success &&
        pageStatus.dashboard.todayTotalQuestion > pageStatus.dashboard.submitTotalQuestion,
    };
  });

  return {
    initPageMeta,
    pageStatus,
    resetState,
    updateStoreData,
    footerBtns,
  };
});
